import { FC, ReactNode, useEffect } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import clsx from 'clsx';
import { MediaLibraryDndType } from 'app/config/mediaLibraryPage';
import {
  AlbumDropResult,
  MediaLibraryDragItem,
} from 'app/modules/mediaLibraryPage/types/Dnd';

interface Props {
  item: MediaLibraryDragItem;
  canDrag?: boolean;
  onDragEnd(item: MediaLibraryDragItem, result: AlbumDropResult | null): void;
  children: ReactNode;
}

const MediaLibraryDragSource: FC<Props> = ({
  item,
  canDrag = true,
  onDragEnd,
  children,
}) => {
  const [{ isDragging }, dragRef, preview] = useDrag(
    () => ({
      type:
        item.kind === 'assets'
          ? MediaLibraryDndType.Assets
          : MediaLibraryDndType.Album,
      item,
      canDrag,
      end: (draggedItem, monitor) =>
        onDragEnd(draggedItem, monitor.getDropResult<AlbumDropResult>()),
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [item, canDrag, onDragEnd],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return (
    <div
      ref={dragRef}
      className={clsx({ 'tw-opacity-50': isDragging })}
      data-cy="media-library-page_drag-source"
      data-drag-kind={item.kind}
      data-dragging={String(isDragging)}
    >
      {children}
    </div>
  );
};

export default MediaLibraryDragSource;
